<script lang="ts">
    import Image from "@lernetz/svelte-image/components/Image.svelte";
    import { requests, setupSharedProps } from "../../setup";
    import { t } from "@lernetz/svelte-translate";
    import { createEventDispatcher } from "svelte";
    import { Icon } from "@lernetz/svelte-icon";
    import { router } from "@inertiajs/svelte";
    import { repeatOnHold } from "../repeatOnHold";

    export let question;
    export let dashboard;
    export let questionNr;
    export let mode = "single";
    export let last = false;
    export let type;

    const dispatch = createEventDispatcher();
    let block = false;
    let shownAnswers = [];
    let showSolution:boolean = false;
    const params = new URLSearchParams(window.location.search);
    const pupils = Number(params.get('pupils') ?? 20);
    const preview = params.get('preview') ?? "false";
    let pupilsA = Math.ceil(pupils / 2);
    let pupilsB = Math.floor(pupils / 2);
    let selectedAnswer = 0;
    let lockAnswers = false;
    let showExplanation = true;

    setupSharedProps();

    $: if (question) {
        prepareAnswers();
    }

    function shuffle<T>(array: T[]): T[] {
        return array
            .map(value => ({ value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort)
            .map(({ value }) => value);
    }

    function prepareAnswers() {
        const answers: Answer[] = [
            { text: question.answer1, correct: question.answer1correct },
            { text: question.answer2, correct: question.answer2correct },
            { text: question.answer3, correct: question.answer3correct },
            { text: question.answer4, correct: question.answer4correct },
        ];

        const correct = answers.find(a => a.correct);
        const wrong = answers.filter(a => !a.correct);

        if (!correct || wrong.length < 1) return;

        const randomWrong = wrong[Math.floor(Math.random() * wrong.length)];

        shownAnswers = shuffle([correct, randomWrong]);
    }

    function nextQuestion() {
        dispatch("next");
    }

    function minusA() {
        pupilsA = pupilsA - 1;
        pupilsB = pupilsB + 1;
    }
    function plusA() {
        pupilsA = pupilsA + 1;
        pupilsB = pupilsB - 1;
    }

    function selectAnswer(answer: number) {
        if(lockAnswers == false && mode == 'single') {
            selectedAnswer = answer;
            lockAnswers = true;
            saveData()
        }
    }

    function saveData() {
        showSolution = true;
        if(preview == "false") {
            let data = {dashboardId: dashboard.id, questionId: question.id, type: type, mode: mode, right: 0, wrong: 0};
            if(mode == "class") {
                if(shownAnswers[0].correct) {
                    data.right = pupilsA;
                    data.wrong = pupilsB;
                }
                else {
                    data.right = pupilsB;
                    data.wrong = pupilsA;
                }
            }
            else {
                const correct = [
                    question.answer1correct,
                    question.answer2correct,
                    question.answer3correct,
                    question.answer4correct
                ][selectedAnswer - 1];
                if(correct) {
                    data.right = 1;
                }
                else {
                    data.wrong = 1;
                }
            }
            dispatch("updateResults", {right: data.right, wrong: data.wrong});
            router.post('/saveData', data)
        }
    }

    $: classChoiceClasses = shownAnswers.map((answer, i) => {
        const correct = answer?.correct;

        if (!showSolution) {
            return {
                border: "border-yellow-main",
                circle: "bg-yellow-accent",
                showIcon: false
            };
        }

        return {
            border: correct ? "border-correct-light" : "border-false-light",
            circle: correct ? "bg-correct-dark" : "bg-false-dark",
            showIcon: true
        };
    });

    $: singleChoiceClasses = [0,1,2,3].map(i => {
        const correct = [
            question.answer1correct,
            question.answer2correct,
            question.answer3correct,
            question.answer4correct
        ][i];

        if (!showSolution) {
            return {
                border: "border-yellow-main",
                circle: "bg-yellow-accent",
                showIcon: false
            };
        }

        const selectedCorrect = [
            question.answer1correct,
            question.answer2correct,
            question.answer3correct,
            question.answer4correct
        ][selectedAnswer - 1];

        // angeklickt & richtig
        if (selectedAnswer === i + 1 && correct) {
            return {
                border: "border-correct-light",
                circle: "bg-correct-dark",
                showIcon: true
            };
        }

        // angeklickt & falsch
        if (selectedAnswer === i + 1 && !correct) {
            return {
                border: "border-false-light",
                circle: "bg-false-dark",
                showIcon: true
            };
        }

        // richtige Antwort anzeigen wenn falsch gewählt wurde
        if (selectedAnswer !== i + 1 && correct && !selectedCorrect) {
            return {
                border: "border-correct-light",
                circle: "bg-correct-dark",
                showIcon: true
            };
        }

        return {
            border: "border-yellow-main",
            circle: "bg-yellow-accent",
            showIcon: false
        };
    });

</script>
{#if mode == 'class'}
    <div class="flex flex-col xl:flex-row gap-16 md:gap-32 xl:items-center xl:justify-center px-16 md:px-24 w-full max-w-[900px] mx-auto">
        <!-- Answer A: below question on mobile, left on desktop -->
        <div class="order-2 xl:order-1">
            <div class="flex items-center gap-12 xl:flex-col xl:items-center xl:gap-0">
                <div class={`w-40 h-40 shrink-0 text-center place-content-center rounded-full text-white text-28 relative z-20 ${classChoiceClasses[0]?.circle}`}>
                    {#if classChoiceClasses[0]?.showIcon}
                        <Icon name={classChoiceClasses[0].circle.includes("false") ? "close" : "check"} class="w-28 h-28 place-self-center text-white fill-white" />
                    {:else}
                        A
                    {/if}
                </div>
                <div class={`bg-white border-8 text-black text-28 p-20 rounded-3xl text-center w-full xl:w-277 font-bold relative xl:-mt-20 ${classChoiceClasses[0]?.border}`}>
                    {shownAnswers[0]?.text}
                </div>
            </div>
            <div class="flex items-center justify-center gap-8 sm:gap-16 mt-24 sm:mt-48">
                <button use:repeatOnHold={minusA} disabled={showSolution || pupilsA <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="minus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
                <input type="number" disabled={true} bind:value={pupilsA} class="border-blue-light focus:border-blue-main border-2 rounded-3xl w-64 sm:w-128 h-56 sm:h-80 text-28 sm:text-36 font-bold text-center appearance-none
     [&::-webkit-outer-spin-button]:appearance-none
     [&::-webkit-inner-spin-button]:appearance-none
     [&::-webkit-outer-spin-button]:m-0
     [&::-webkit-inner-spin-button]:m-0"/>
                <button use:repeatOnHold={plusA} disabled={showSolution || pupilsB <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="plus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
            </div>
        </div>
        <!-- Question box: top on mobile, center on desktop -->
        <div class="order-1 xl:order-2 w-full xl:w-524 xl:shrink-0">
            <div>
                <div class="text-18 font-bold bg-line-subtle rounded-2xl px-10 text-grey-1 w-fit py-4 place-self-center relative z-20">{$t('question')} {questionNr + 1} / 10</div>
                <div class="font-bold text-28 bg-white py-32 text-blue-main rounded-3xl -mt-20 relative z-10 px-16">
                    <div class="text-center">{question.question}</div>
                    {#if showSolution}
                        {#if showExplanation}
                        <div class="mt-24 bg-yellow-main rounded-2xl px-16 pb-24 pt-16 text-black text-22 flex flex-col">
                            <div class="place-self-center">
                                <Icon name="bulb" class="w-28 h-28 place-self-center"></Icon>
                            </div>
                            <div class="text-center">{question.explanation}</div>
                            <button class="bg-yellow-accent w-32 h-32 self-end mt-24 rounded-lg" on:click={() => showExplanation = false}><Icon name="arrow_refresh" class="w-28 h-28 place-self-center"></Icon></button>
                        </div>
                    {:else}
                        <div class="flex flex-col">
                            <Image class="rounded-3xl w-full aspect-square mt-24 object-contain" request={requests.assets} vars={{ file_name: question.image?.file_name, preset: "medium.png" }} />
                            <button class="bg-yellow-accent w-32 h-32 self-end rounded-lg mt-24" on:click={() => showExplanation = true}><Icon name="arrow_refresh" class="w-28 h-28 place-self-center"></Icon></button>
                        </div>
                    {/if}
                    {:else}
                        <Image class="rounded-3xl w-full aspect-square mt-24 object-contain" request={requests.assets} vars={{ file_name: question.image?.file_name, preset: "medium.png" }} />
                    {/if}
                </div>
            </div>
        </div>
        <!-- Answer B: below A on mobile, right on desktop -->
        <div class="order-3">
            <div class="flex items-center gap-12 xl:flex-col xl:items-center xl:gap-0">
                <div class={`w-40 h-40 shrink-0 text-center place-content-center rounded-full text-white text-28 relative z-20 ${classChoiceClasses[1]?.circle}`}>
                    {#if classChoiceClasses[1]?.showIcon}
                        <Icon name={classChoiceClasses[1].circle.includes("false") ? "close" : "check"} class="w-28 h-28 place-self-center text-white fill-white" />
                    {:else}
                        B
                    {/if}
                </div>
                <div class={`bg-white border-8 text-black text-28 p-20 rounded-3xl text-center w-full xl:w-277 font-bold relative xl:-mt-20 ${classChoiceClasses[1]?.border}`}>
                    {shownAnswers[1]?.text}
                </div>
            </div>
            <div class="flex items-center justify-center gap-8 sm:gap-16 mt-24 sm:mt-48">
                <button use:repeatOnHold={plusA} disabled={showSolution || pupilsB <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="minus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
                <input type="number" disabled={true} bind:value={pupilsB} class="border-blue-light focus:border-blue-main border-2 rounded-3xl w-64 sm:w-128 h-56 sm:h-80 text-28 sm:text-36 font-bold text-center appearance-none
     [&::-webkit-outer-spin-button]:appearance-none
     [&::-webkit-inner-spin-button]:appearance-none
     [&::-webkit-outer-spin-button]:m-0
     [&::-webkit-inner-spin-button]:m-0"/>
                <button use:repeatOnHold={minusA} disabled={showSolution || pupilsA <= 0} class="disabled:opacity-30 touch-none select-none p-8"><Icon name="plus" class="w-28 h-28 place-self-center text-blue-main"></Icon></button>
            </div>
        </div>
    </div>
    <div class="mt-32 md:mt-64 xl:mt-112 px-16 md:px-24">
        <button class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg" class:hidden={showSolution} on:click={saveData}>{$t('show solution')}</button>
        <button class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg flex gap-12" class:hidden={!showSolution} on:click={nextQuestion}>{$t('next')}
            <Icon name="arrow-right" class="w-28 h-28 place-self-center"></Icon>
        </button>
    </div>
{:else}
<div class="mt-32 flex flex-col items-center justify-center px-16 md:px-24 w-full max-w-[900px] mx-auto">
    <div class="w-full xl:w-524">
        <div>
            <div class="text-18 font-bold bg-line-subtle rounded-2xl px-10 text-grey-1 w-fit py-4 place-self-center relative z-20">{$t('question')} {questionNr + 1} / 10</div>
            <div class="font-bold text-28 bg-white py-32 text-blue-main rounded-3xl -mt-20 relative z-10 px-16">
                <div class="text-center">{question.question}</div>
                {#if showSolution}
                    {#if showExplanation}
                    <div class="mt-24 bg-yellow-main rounded-2xl px-16 pb-24 pt-16 text-black text-22 flex flex-col">
                        <div class="place-self-center">
                            <Icon name="bulb" class="w-28 h-28 place-self-center"></Icon>
                        </div>
                        <div class="text-center">{question.explanation}</div>
                        <button class="bg-yellow-accent w-32 h-32 self-end mt-24 rounded-lg" on:click={() => showExplanation = false}><Icon name="arrow_refresh" class="w-28 h-28 place-self-center"></Icon></button>
                    </div>
                {:else}
                    <div class="flex flex-col">
                        <Image class="rounded-3xl w-full aspect-square mt-24 object-contain" request={requests.assets} vars={{ file_name: question.image?.file_name, preset: "medium.png" }} />
                        <button class="bg-yellow-accent w-32 h-32 self-end rounded-lg mt-24" on:click={() => showExplanation = true}><Icon name="arrow_refresh" class="w-28 h-28 place-self-center"></Icon></button>
                    </div>
                {/if}
                {:else}
                    <Image class="rounded-3xl w-full aspect-square mt-24 object-contain" request={requests.assets} vars={{ file_name: question.image?.file_name, preset: "medium.png" }} />
                {/if}
            </div>
        </div>
    </div>
    <div class="flex flex-col xl:flex-wrap xl:flex-row gap-x-16 gap-y-12 md:gap-x-32 md:gap-y-24 xl:max-w-672 w-full xl:items-center xl:justify-center mt-32 md:mt-64">
        {#each [0,1,2,3] as i (i)}
        <div class="flex items-center gap-12 xl:gap-0">
            <div class={`w-40 h-40 shrink-0 text-center place-content-center rounded-full text-white text-28 relative z-20 ${singleChoiceClasses[i].circle}`}>
                {#if singleChoiceClasses[i].showIcon}
                    <Icon
                        name={singleChoiceClasses[i].circle.includes("false") ? "close" : "check"}
                        class="w-28 h-28 place-self-center text-white fill-white"
                    />
                {:else}
                    {String.fromCharCode(65 + i)}
                {/if}
            </div>
            <button
                class={`border-8 text-22 p-20 rounded-3xl text-center w-full xl:w-277 font-bold relative xl:-ml-20
                ${selectedAnswer == (i+1) ? 'bg-blue-main text-white' : 'bg-white text-black'}
                ${singleChoiceClasses[i].border}`}
                on:click={() => selectAnswer(i+1)}
            >
                {[question.answer1, question.answer2, question.answer3, question.answer4][i]}
            </button>
        </div>
        {/each}
    </div>
</div>
<div class="mt-32 md:mt-64 xl:mt-112 px-16 md:px-24">
    <button class="bg-blue-main text-white text-18 font-bold py-12 px-24 rounded-lg flex gap-12" class:hidden={!showSolution} on:click={nextQuestion}>{$t('next')}
        <Icon name="arrow-right" class="w-28 h-28 place-self-center"></Icon>
    </button>
</div>

{/if}
